import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Send } from "lucide-react";
import { Card, PageHeader, Btn, Field, inputCls, StatusPill, Empty } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const MAX = 6 * 1024 * 1024;

export default function Tasks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [uploading, setUploading] = useState("");
  const inputs = useRef({});

  const load = () => api.get("/tasks").then((r) => setRows(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const update = async (id, payload) => {
    try {
      await api.put(`/tasks/${id}`, payload);
      toast.success("Task updated.");
      setNotes((n) => ({ ...n, [id]: "" }));
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const uploadFor = async (task, files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > MAX) {
      toast.error(`${file.name} is larger than 6MB.`);
      return;
    }
    setUploading(task.id);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data: media } = await api.post("/media", {
        filename: file.name, category: "Other", mime_type: file.type,
        size: file.size, data_url: dataUrl, description: `Task: ${task.title}`,
      });
      await api.put(`/tasks/${task.id}`, {
        media_ids: [...(task.media_ids || []), media.id],
        status: "Submitted",
        note: `Uploaded ${file.name}`,
      });
      toast.success("File uploaded and task submitted.");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setUploading("");
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <PageHeader
        title="My Tasks"
        body="Things PostMint needs from you, in one list. Upload what's asked for and we'll take it from there."
      />

      {loading ? (
        <Card testId="tasks-loading">Loading your tasks…</Card>
      ) : rows.length === 0 ? (
        <Empty testId="tasks-empty" title="Nothing to do right now" body="When we need something from you, it will appear here." />
      ) : (
        <div className="space-y-4" data-testid="tasks-list">
          {rows.map((t) => (
            <Card key={t.id} testId={`task-card-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-base font-semibold text-slate-900">{t.title}</p>
                  {t.description && <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{t.description}</p>}
                  <p className="mt-2 text-xs text-slate-500">
                    {t.due_date ? `Due ${t.due_date}` : "No due date"} · {t.priority}
                    {t.media_ids?.length ? ` · ${t.media_ids.length} file(s) attached` : ""}
                  </p>
                </div>
                <StatusPill status={t.status} testId={`task-status-${t.id}`} />
              </div>

              {t.comments?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {t.comments.map((c) => (
                    <div key={c.id} className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      {c.note}
                      <span className="block text-xs text-slate-400">
                        {c.role === "admin" ? "PostMint" : "You"} · {new Date(c.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4">
                <Field label="Add a note" htmlFor={`task-note-${t.id}`}>
                  <textarea
                    id={`task-note-${t.id}`}
                    rows={2}
                    data-testid={`task-note-input-${t.id}`}
                    className={inputCls}
                    value={notes[t.id] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [t.id]: e.target.value }))}
                  />
                </Field>
              </div>

              <input
                ref={(el) => { inputs.current[t.id] = el; }}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.pdf"
                className="hidden"
                data-testid={`task-file-input-${t.id}`}
                onChange={(e) => uploadFor(t, e.target.files)}
              />

              <div className="mt-4 flex flex-wrap gap-2">
                <Btn variant="outline" testId={`task-upload-button-${t.id}`} disabled={uploading === t.id} onClick={() => inputs.current[t.id]?.click()}>
                  <Paperclip className="h-4 w-4" /> {uploading === t.id ? "Uploading…" : "Upload file"}
                </Btn>
                <Btn variant="outline" testId={`task-progress-button-${t.id}`} onClick={() => update(t.id, { status: "In Progress", note: notes[t.id] })}>
                  Mark in progress
                </Btn>
                <Btn testId={`task-submit-button-${t.id}`} onClick={() => update(t.id, { status: "Submitted", note: notes[t.id] })}>
                  <Send className="h-4 w-4" /> Submit to PostMint
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
